<template>
  <div>
    <PageHeader :title="t('title')">
      <SecondaryButton v-if="allowedToAjustMonthTarget && !hasMultipleStores" @click="editTargetModalOpen = true">
        {{ t("adjust_target") }}
      </SecondaryButton>
    </PageHeader>

    <div v-if="roadmapPerformance">
      <div class="flex justify-between items-center">
        <p class="text-md text-black font-medium">{{ quote }}</p>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 my-4">
        <DailyTargetBlock
          :current="dailyTarget?.current"
          :target="dailyTarget?.target"
          :leads-waiting="dailyTarget?.leadsWaitingForConfirmation"
        />
        <DailyBlock
          :current="dailyTarget?.current"
          :target="dailyTarget?.target"
          :leads-waiting="dailyTarget?.leadsWaitingForConfirmation"
        />
        <YesterdayBlock
          :current="yesterdayPerformance?.current"
          :target="yesterdayPerformance?.target"
          :leads-waiting="yesterdayPerformance?.leadsWaitingForConfirmation"
        />
      </div>
      <div class="grid grid-cols-1 gap-4">
        <MonthlyTargetBlock
          :current="monthlyPerformance?.current"
          :target="monthlyPerformance?.target"
          :projection="monthlyPerformance?.projection"
          :leads-waiting="leadsWaitingForConfirmation"
        />
        <BoosterBlock
          v-if="retributionsBoostEnabled() && !hasMultipleStores"
          :current="monthlyPerformance?.current"
          :projection="monthlyPerformance?.projection"
        />
      </div>
      <div class="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StoreRetributionsRanking
          :sellersWithRetributionRanking="sellerRanking"
          :title="t('sellers_ranking')"
          emoji="👥"
        />
        <StoreRankingDetails :storeRanking="storeRanking" :title="t('network_ranking')" subtitle=" " emoji="📊" />
      </div>
      <Modal v-model="editTargetModalOpen" :open="editTargetModalOpen" @close="editTargetModalOpen = false">
        <StoreEditMonthlySalesTargetForm :store="selectedStoreWithMonthlySalesTarget" @updated="monthlyTargetUpdated" />
      </Modal>
    </div>
    <Loading v-else />
  </div>
</template>

<script>
import DailyTargetBlock from "@/components/roadmap/DailyTargetBlock.vue";
import DailyBlock from "@/components/roadmap/DailyBlock.vue";
import YesterdayBlock from "@/components/roadmap/YesterdayBlock.vue";
import MonthlyTargetBlock from "@/components/roadmap/MonthlyTargetBlock.vue";
import BoosterBlock from "@/components/roadmap/BoosterBlock.vue";
import StoreRetributionsRanking from "@/components/store/StoreRetributionsRanking.vue";
import StoreRankingDetails from "@/components/roadmap/StoreRankingDetails.vue";
import AnalyticsService from "@/api/services/analytics";
import StoreEditMonthlySalesTargetForm from "@/components/store/StoreEditMonthlySalesTargetForm.vue";
import { Loading, SecondaryButton, Modal, PageHeader, toCamelCase } from "@estaly/ui";
import { useI18n } from "vue-i18n";
import { mapGetters } from "vuex";

export default {
  components: {
    DailyTargetBlock,
    DailyBlock,
    YesterdayBlock,
    MonthlyTargetBlock,
    BoosterBlock,
    Loading,
    StoreRetributionsRanking,
    StoreRankingDetails,
    SecondaryButton,
    PageHeader,
    Modal,
    StoreEditMonthlySalesTargetForm,
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data: () => ({
    roadmapPerformance: null,
    quote: null,
    dailyTarget: null,
    yesterdayPerformance: null,
    monthlyPerformance: null,
    sellerRanking: null,
    storeRanking: null,
    leadsWaitingForConfirmation: 0,
    editTargetModalOpen: false,
  }),
  computed: {
    ...mapGetters("store", ["stores", "selectedStore", "hasMultipleStores", "retributionsBoostEnabled"]),
    ...mapGetters("auth", ["hasAccessToAdminDashboard", "isResponsible"]),
    monthlySalesTarget() {
      return this.monthlyPerformance?.target;
    },
    selectedStoreWithMonthlySalesTarget() {
      return {
        ...this.selectedStore,
        monthlySalesTarget: this.monthlySalesTarget || 0,
      };
    },
    allowedToAjustMonthTarget() {
      return this.monthlySalesTarget > 0 && (this.isResponsible || this.hasAccessToAdminDashboard);
    },
  },
  mounted() {
    this.fetchRoadmapPerformance();
  },
  methods: {
    async fetchRoadmapPerformance() {
      const response = await AnalyticsService.getRoadmapPerformance();
      if (!response?.success) return;
      const data = toCamelCase(response.data);
      this.roadmapPerformance = data;
      this.quote = data.quote;
      this.dailyTarget = data.dailyTarget;
      this.yesterdayPerformance = data.yesterdayPerformance;
      this.monthlyPerformance = data.monthlyPerformance;
      this.sellerRanking = data.sellerRanking;
      this.storeRanking = data.storeRanking;
      this.leadsWaitingForConfirmation = data.leadsWaitingForConfirmation ?? 0;
    },
    async monthlyTargetUpdated() {
      this.editTargetModalOpen = false;
      await this.fetchRoadmapPerformance();
    },
  },
};
</script>

<i18n src="./StoreRoadmapOverview.i18n.json"></i18n>
